import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../../lib/supabase';
import EditableTable from './EditableTable';

/**
 * Editor for `public.metric_thresholds`.
 * Server-scoped. metric_name and source_module are read-only (source of truth).
 * Only target_value is editable.
 */
const MetricThresholdsTab = ({ server }) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!server) return;
    setLoading(true);
    setError(null);
    const { data, error: dbError } = await supabase
      .from('metric_thresholds')
      .select('*')
      .eq('server', server)
      .order('source_module', { ascending: true })
      .order('metric_name', { ascending: true });
    if (dbError) setError(dbError.message);
    setRows(data || []);
    setLoading(false);
  }, [server]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = [
    {
      key: 'source_module',
      label: t(
        'superadmin.businessRules.thresholds.sourceModule',
        'Source module'
      ),
      type: 'text',
      readonly: true,
      width: '220px',
      render: (v) => (
        <span className="font-data text-xs text-muted-foreground">
          {v ?? '—'}
        </span>
      ),
    },
    {
      key: 'metric_name',
      label: t('superadmin.businessRules.thresholds.metricName', 'Metric name'),
      type: 'text',
      readonly: true,
      render: (v) => <span className="font-data text-foreground">{v}</span>,
    },
    {
      key: 'target_value',
      label: t('superadmin.businessRules.thresholds.targetValue', 'Target value'),
      type: 'decimal',
      width: '160px',
    },
  ];

  const handleSave = useCallback(
    async (row, patch) => {
      const { error: dbError } = await supabase
        .from('metric_thresholds')
        .update(patch)
        .eq('id', row.id);
      if (dbError) throw dbError;
      await load();
    },
    [load]
  );

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        {t(
          'superadmin.businessRules.thresholds.intro',
          'Module-level thresholds (Key Metrics targets, onboarding rules, alert thresholds). metric_name and source_module are immutable — only target_value can be tuned per server.'
        )}
      </p>
      {error && (
        <div className="rounded-xl border border-error/30 bg-error/5 px-4 py-2 text-sm text-error">
          {error}
        </div>
      )}
      <EditableTable
        columns={columns}
        rows={rows}
        loading={loading}
        onRowSave={handleSave}
        emptyMessage={t(
          'superadmin.businessRules.thresholds.empty',
          'No thresholds configured for this server yet. Add them via SQL migration.'
        )}
      />
    </div>
  );
};

export default MetricThresholdsTab;
